import type { ModelViewerElement } from '@google/model-viewer';

/**
 * Camera orbit, zoom limits, focus targets, and reset behaviour for `<model-viewer>`.
 *
 * Pipeline:
 * 1. `MODEL_VIEWER_RELAXED_ZOOM_ATTRS` — placeholder limits before GLB load
 * 2. `applyDynamicModelViewerZoomLimits` — scale min/max orbit & FOV to bounding box
 * 3. `resolveAnnotationNavigationCamera` / `applyAnnotationNavigationCamera` — hotspot fly-to
 * 4. `resetModelViewerCamera` — restore saved or default framing
 */

// --- Orbit radius & field-of-view scaling factors ---

/** Orbit radius limits as multiples of the model bounding-box longest edge (metres). */
export const MODEL_VIEWER_ZOOM_FACTORS = {
  MIN_ORBIT_RADIUS_FACTOR: 0.02,
  /**
   * Max zoom-out distance relative to model size.
   * model-viewer tap-to-recenter (miss) jumps to this max — keep it modest.
   */
  MAX_ORBIT_RADIUS_FACTOR: 6.5,
  /** Absolute floor so tiny models still allow close-up without zero/NaN. */
  MIN_ORBIT_RADIUS_FLOOR_M: 0.01,
  /** Hard ceiling for max orbit (tap-to-recenter cannot exceed this). */
  MAX_ORBIT_RADIUS_CAP_M: 95,
  MIN_FIELD_OF_VIEW_DEG: 5,
  MAX_FIELD_OF_VIEW_DEG: 75,
} as const;

export type ModelViewerCameraDefaults = {
  cameraOrbit?: string;
  cameraTarget?: string;
  cameraFieldOfView?: string;
};

export type ModelViewerZoomLimits = {
  maxDimM: number;
  minOrbitRadiusM: number;
  maxOrbitRadiusM: number;
  minFieldOfViewDeg: number;
  maxFieldOfViewDeg: number;
};

type ParsedCameraOrbit = {
  theta: number;
  phi: number;
  radius: number;
};

/** Clamp a numeric camera parameter without changing inclusive bounds semantics. */
function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function radiansToDegrees(rad: number): number {
  return (rad * 180) / Math.PI;
}

function formatMeters(m: number): string {
  if (m < 0.01) return m.toFixed(4);
  if (m < 1) return m.toFixed(3);
  return m.toFixed(2);
}

/** Longest edge of the model axis-aligned bounding box (metres). */
export function getModelMaxDimensionM(mv: ModelViewerElement): number {
  const { x, y, z } = mv.getDimensions();
  return Math.max(x, y, z, 0.001);
}

export function computeZoomLimitsFromMaxDimension(maxDimM: number): ModelViewerZoomLimits {
  const maxDim = Math.max(maxDimM, 0.001);
  const {
    MIN_ORBIT_RADIUS_FACTOR,
    MAX_ORBIT_RADIUS_FACTOR,
    MIN_ORBIT_RADIUS_FLOOR_M,
    MAX_ORBIT_RADIUS_CAP_M,
    MIN_FIELD_OF_VIEW_DEG,
    MAX_FIELD_OF_VIEW_DEG,
  } = MODEL_VIEWER_ZOOM_FACTORS;

  const minOrbitRadiusM = Math.max(maxDim * MIN_ORBIT_RADIUS_FACTOR, MIN_ORBIT_RADIUS_FLOOR_M);
  const maxOrbitRadiusM = Math.min(maxDim * MAX_ORBIT_RADIUS_FACTOR, MAX_ORBIT_RADIUS_CAP_M);

  return {
    maxDimM: maxDim,
    minOrbitRadiusM,
    maxOrbitRadiusM: Math.max(maxOrbitRadiusM, minOrbitRadiusM * 4),
    minFieldOfViewDeg: MIN_FIELD_OF_VIEW_DEG,
    maxFieldOfViewDeg: MAX_FIELD_OF_VIEW_DEG,
  };
}

function formatOrbitLimitAttr(radiusM: number): string {
  return `auto auto ${formatMeters(radiusM)}m`;
}

// --- Orbit / FOV string parsing & formatting ---

const CAMERA_ORBIT_DEG_RE = /^(-?[\d.]+)deg\s+(-?[\d.]+)deg\s+([\d.]+)m$/;
const CAMERA_ORBIT_RAD_RE = /^(-?[\d.]+)rad\s+(-?[\d.]+)rad\s+([\d.]+)m$/;

// --- Annotation navigation: focus distance & orbit angles ---

/** Comfortable viewing distance when jumping to an annotation (fraction of model size). */
const ANNOTATION_NAV_MIN_ORBIT_RADIUS_FACTOR = 0.24;

/** Absolute minimum orbit radius when navigating — avoids clipping into surface hotspots. */
const ANNOTATION_NAV_MIN_ORBIT_RADIUS_ABSOLUTE_M = 0.22;

/** Default orbit distance when saved pivot/target is missing (fraction of model size). */
const ANNOTATION_NAV_FALLBACK_ORBIT_RADIUS_FACTOR = 0.4;

/** Minimum FOV when navigating (wider angle = less extreme zoom-in). */
const ANNOTATION_NAV_MIN_FIELD_OF_VIEW_DEG = 34;

/** Clamp vertical orbit angle so the hotspot stays near the viewport centre. */
const ANNOTATION_NAV_MIN_PHI_DEG = 25;
const ANNOTATION_NAV_MAX_PHI_DEG = 85;

export type AnnotationNavigationCamera = {
  cameraTarget: string;
  cameraOrbit: string;
  cameraFieldOfView: string;
};

export function parseCameraOrbitString(orbit: string): ParsedCameraOrbit | null {
  const trimmed = orbit.trim();
  const degMatch = trimmed.match(CAMERA_ORBIT_DEG_RE);
  if (degMatch) {
    return {
      theta: parseFloat(degMatch[1]),
      phi: parseFloat(degMatch[2]),
      radius: parseFloat(degMatch[3]),
    };
  }

  const radMatch = trimmed.match(CAMERA_ORBIT_RAD_RE);
  if (radMatch) {
    return {
      theta: radiansToDegrees(parseFloat(radMatch[1])),
      phi: radiansToDegrees(parseFloat(radMatch[2])),
      radius: parseFloat(radMatch[3]),
    };
  }

  return null;
}

export function parseFieldOfViewDeg(fov: string): number | null {
  const match = fov.trim().match(/^([\d.]+)deg$/);
  return match ? parseFloat(match[1]) : null;
}

export function formatCameraOrbitString(theta: number, phi: number, radiusM: number): string {
  return `${theta}deg ${phi}deg ${formatMeters(radiusM)}m`;
}

export function formatFieldOfViewDeg(deg: number): string {
  return `${Number(deg.toFixed(2))}deg`;
}

/** How long camera-controls stay disabled during annotation fly-to. */
export const ANNOTATION_CAMERA_TRANSITION_SETTLE_MS = 320;

const MODEL_VIEWER_DEFAULT_INTERPOLATION_DECAY_MS = 50;

// --- Camera state application (orbit, target, FOV) ---

/** Restores model-viewer default orbit/zoom responsiveness (not annotation fly-to). */
export function ensureDefaultCameraInterpolation(mv: ModelViewerElement): void {
  mv.interpolationDecay = MODEL_VIEWER_DEFAULT_INTERPOLATION_DECAY_MS;
  mv.removeAttribute('interpolation-decay');
}

export type ApplyModelViewerCameraOptions = {
  /** Snap instantly (reset). Omit or false for damper-smoothed transition. */
  immediate?: boolean;
};

function syncModelViewerCameraProperty(
  mv: ModelViewerElement,
  attribute: 'camera-target' | 'camera-orbit' | 'field-of-view',
  property: 'cameraTarget' | 'cameraOrbit' | 'fieldOfView',
  value: string
): void {
  mv[property] = value;
  mv.setAttribute(attribute, value);
}

function jumpCameraToGoalIfAvailable(mv: ModelViewerElement): void {
  const jump = (mv as ModelViewerElement & { jumpCameraToGoal?: () => void }).jumpCameraToGoal;
  if (typeof jump === 'function') {
    jump.call(mv);
  }
}

/** Applies orbit, target, and FOV on model-viewer. */
export function applyModelViewerCameraState(
  mv: ModelViewerElement,
  { cameraTarget, cameraOrbit, cameraFieldOfView }: AnnotationNavigationCamera,
  options: ApplyModelViewerCameraOptions = {}
): void {
  const { immediate = false } = options;

  syncModelViewerCameraProperty(mv, 'camera-target', 'cameraTarget', cameraTarget);
  syncModelViewerCameraProperty(mv, 'camera-orbit', 'cameraOrbit', cameraOrbit);
  syncModelViewerCameraProperty(mv, 'field-of-view', 'fieldOfView', cameraFieldOfView);

  if (immediate) {
    jumpCameraToGoalIfAvailable(mv);
  }
}

/**
 * Smooth glide to a saved annotation view.
 * Uses model-viewer damper without jumpCameraToGoal — does not touch interpolation-decay
 * so manual orbit/zoom stays at the default responsiveness.
 */
export function applyAnnotationNavigationCamera(
  mv: ModelViewerElement,
  camera: AnnotationNavigationCamera
): void {
  ensureDefaultCameraInterpolation(mv);
  applyModelViewerCameraState(mv, camera, { immediate: false });
}

/**
 * Resolves camera state for programmatic annotation navigation.
 * When orbit + target were saved at placement time, restores that exact view.
 */
export function resolveAnnotationNavigationCamera(
  mv: ModelViewerElement,
  annotation: {
    orbit?: string;
    target?: string;
    position: string;
    cameraFieldOfView?: string;
  },
  defaults: { orbit: string; fieldOfView: string }
): AnnotationNavigationCamera {
  const limits = computeZoomLimitsFromMaxDimension(getModelMaxDimensionM(mv));
  const savedTarget = annotation.target?.trim();
  const savedOrbit = annotation.orbit?.trim();
  const parsedSavedOrbit = savedOrbit ? parseCameraOrbitString(savedOrbit) : null;
  const hasExactSnapshot = Boolean(savedTarget && parsedSavedOrbit);

  if (hasExactSnapshot && parsedSavedOrbit) {
    const rawFov = annotation.cameraFieldOfView?.trim() || defaults.fieldOfView;
    let fovDeg = parseFieldOfViewDeg(rawFov) ?? 30;
    fovDeg = clampNumber(fovDeg, limits.minFieldOfViewDeg, limits.maxFieldOfViewDeg);

    const radius = clampNumber(
      parsedSavedOrbit.radius,
      limits.minOrbitRadiusM,
      limits.maxOrbitRadiusM
    );

    return {
      cameraTarget: savedTarget!,
      cameraOrbit: formatCameraOrbitString(
        parsedSavedOrbit.theta,
        parsedSavedOrbit.phi,
        radius
      ),
      cameraFieldOfView: formatFieldOfViewDeg(fovDeg),
    };
  }

  const navMinRadius = Math.max(
    limits.minOrbitRadiusM,
    limits.maxDimM * ANNOTATION_NAV_MIN_ORBIT_RADIUS_FACTOR,
    ANNOTATION_NAV_MIN_ORBIT_RADIUS_ABSOLUTE_M
  );
  const navFallbackRadius = Math.max(
    navMinRadius,
    limits.maxDimM * ANNOTATION_NAV_FALLBACK_ORBIT_RADIUS_FACTOR
  );

  const hasSavedTarget = Boolean(savedTarget);
  const cameraTarget = hasSavedTarget ? savedTarget! : annotation.position;

  const rawOrbit = savedOrbit || defaults.orbit;
  const parsed = parseCameraOrbitString(rawOrbit);

  const theta = parsed?.theta ?? 45;
  const phi = clampNumber(parsed?.phi ?? 65, ANNOTATION_NAV_MIN_PHI_DEG, ANNOTATION_NAV_MAX_PHI_DEG);

  let radius: number;
  if (hasSavedTarget && parsed) {
    radius = clampNumber(parsed.radius, navMinRadius, limits.maxOrbitRadiusM);
  } else {
    radius = Math.min(navFallbackRadius, limits.maxOrbitRadiusM);
  }

  const rawFov = annotation.cameraFieldOfView?.trim() || defaults.fieldOfView;
  let fovDeg = parseFieldOfViewDeg(rawFov) ?? 30;
  fovDeg = clampNumber(
    fovDeg,
    Math.max(ANNOTATION_NAV_MIN_FIELD_OF_VIEW_DEG, limits.minFieldOfViewDeg),
    limits.maxFieldOfViewDeg
  );

  return {
    cameraTarget,
    cameraOrbit: formatCameraOrbitString(theta, phi, radius),
    cameraFieldOfView: formatFieldOfViewDeg(fovDeg),
  };
}

// --- Reset & runtime zoom limit application ---

const DEFAULT_RESET_CAMERA_TARGET = 'auto auto auto';
const DEFAULT_RESET_CAMERA_ORBIT = 'auto auto auto';
const DEFAULT_RESET_FIELD_OF_VIEW = 'auto';

function resolveResetCameraState(defaults?: ModelViewerCameraDefaults): AnnotationNavigationCamera {
  return {
    cameraTarget: defaults?.cameraTarget?.trim() || DEFAULT_RESET_CAMERA_TARGET,
    cameraOrbit: defaults?.cameraOrbit?.trim() || DEFAULT_RESET_CAMERA_ORBIT,
    cameraFieldOfView: defaults?.cameraFieldOfView?.trim() || DEFAULT_RESET_FIELD_OF_VIEW,
  };
}

/** Restore the default product framing or saved initial camera values (instant snap). */
export function resetModelViewerCamera(
  mv: ModelViewerElement,
  defaults?: ModelViewerCameraDefaults
): void {
  applyModelViewerCameraState(mv, resolveResetCameraState(defaults), { immediate: true });
}

/**
 * Apply scale-aware min/max orbit radius and field-of-view after the GLB has loaded.
 * Keeps pinch-zoom and scroll-wheel zoom usable for both small parts and large scenes.
 */
export function applyDynamicModelViewerZoomLimits(mv: ModelViewerElement): ModelViewerZoomLimits | null {
  try {
    const limits = computeZoomLimitsFromMaxDimension(getModelMaxDimensionM(mv));
    mv.setAttribute('min-camera-orbit', formatOrbitLimitAttr(limits.minOrbitRadiusM));
    mv.setAttribute('max-camera-orbit', formatOrbitLimitAttr(limits.maxOrbitRadiusM));
    mv.setAttribute('min-field-of-view', `${limits.minFieldOfViewDeg}deg`);
    mv.setAttribute('max-field-of-view', `${limits.maxFieldOfViewDeg}deg`);
    return limits;
  } catch (error) {
    console.warn('Could not apply dynamic model-viewer zoom limits:', error);
    return null;
  }
}
